"use client";

import { Trans, useLingui } from "@lingui/react/macro";

import { Check, ChevronDown, X } from "lucide-react";
import { useMemo, useState } from "react";
import { cn } from "../../lib/utils";
import { Button } from "../ui/button";
import {
	Command,
	CommandEmpty,
	CommandGroup,
	CommandInput,
	CommandItem,
	CommandList,
} from "../ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import type { LabeledOption } from "./shared";

export type FacetOption = LabeledOption;

type SingleProps = {
	mode: "single";
	value: string | null;
	onChange: (next: string | null) => void;
};

type MultiProps = {
	mode: "multi";
	value: string[];
	onChange: (next: string[]) => void;
};

type FacetPickerProps = (SingleProps | MultiProps) & {
	options: FacetOption[];
	placeholder: string;
	searchPlaceholder: string;
	disabled?: boolean;
	emptyLabel?: string;
};

const isSingle = (p: FacetPickerProps): p is FacetPickerProps & SingleProps =>
	p.mode === "single";

export const TherapistDirectoryFacetPicker = (props: FacetPickerProps) => {
	const { t } = useLingui();
	const [open, setOpen] = useState(false);
	const [query, setQuery] = useState("");
	const lowered = query.trim().toLowerCase();

	const filtered = useMemo(
		() =>
			lowered
				? props.options.filter((o) => o.label.toLowerCase().includes(lowered))
				: props.options,
		[props.options, lowered],
	);

	const triggerLabel = useMemo(() => {
		if (isSingle(props)) {
			return (
				props.options.find((o) => o.id === props.value)?.label ??
				props.placeholder
			);
		}
		if (props.value.length === 0) {
			return props.placeholder;
		}
		if (props.value.length === 1) {
			return (
				props.options.find((o) => o.id === props.value[0])?.label ??
				`${props.placeholder} · 1`
			);
		}
		return `${props.placeholder} · ${props.value.length}`;
	}, [props]);

	const isSelected = (id: string) =>
		isSingle(props) ? props.value === id : props.value.includes(id);

	const onItemSelect = (id: string) => {
		if (isSingle(props)) {
			props.onChange(id);
			setOpen(false);
			setQuery("");
			return;
		}
		const next = props.value.includes(id)
			? props.value.filter((v) => v !== id)
			: [...props.value, id];
		props.onChange(next);
	};

	const showInlineClear = isSingle(props)
		? props.value !== null
		: props.value.length > 0;

	const onClear = () => {
		if (isSingle(props)) {
			props.onChange(null);
		} else {
			props.onChange([]);
		}
	};

	return (
		<Popover
			open={open}
			onOpenChange={(next) => {
				setOpen(next);
				if (!next) {
					setQuery("");
				}
			}}
		>
			<div className="relative w-full">
				<PopoverTrigger asChild>
					<Button
						type="button"
						variant="unstyled"
						rounding="md"
						size="default"
						disabled={props.disabled}
						aria-expanded={open}
						aria-haspopup="listbox"
						className={cn(
							"flex w-full cursor-pointer items-center justify-between border border-klarify-neutral-200 bg-white px-3 py-2 text-left font-normal text-sm shadow-sm transition-colors hover:border-klarify-neutral-300 hover:bg-klarify-neutral-50 disabled:cursor-not-allowed disabled:opacity-60",
							showInlineClear ? "pr-10" : "",
							showInlineClear
								? "text-klarify-neutral-900"
								: "text-klarify-neutral-500",
						)}
					>
						<span className="truncate">{triggerLabel}</span>
						{!showInlineClear ? (
							<ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
						) : null}
					</Button>
				</PopoverTrigger>
				{showInlineClear ? (
					<Button
						type="button"
						variant="ghost"
						size="icon"
						onClick={onClear}
						aria-label={t`Clear ${{ label: props.placeholder }}`}
						className="absolute top-1/2 right-2 h-6 w-6 -translate-y-1/2 cursor-pointer rounded-full text-klarify-neutral-400 hover:bg-klarify-neutral-100 hover:text-klarify-neutral-700"
					>
						<X className="h-3.5 w-3.5" />
					</Button>
				) : null}
			</div>
			<PopoverContent
				className="w-(--radix-popover-trigger-width) p-0"
				align="start"
			>
				<Command shouldFilter={false}>
					<CommandInput
						placeholder={props.searchPlaceholder}
						value={query}
						onValueChange={setQuery}
					/>
					<CommandList>
						<CommandEmpty>
							<span className="block px-3 py-6 text-center text-klarify-neutral-500 text-sm">
								{props.emptyLabel ?? <Trans>No matches</Trans>}
							</span>
						</CommandEmpty>
						<CommandGroup>
							{filtered.map((opt) => (
								<CommandItem
									key={opt.id}
									value={opt.id}
									onSelect={() => onItemSelect(opt.id)}
									className="cursor-pointer"
								>
									<span className="flex-1 truncate">{opt.label}</span>
									{isSelected(opt.id) ? (
										<Check className="ml-2 h-4 w-4 text-klarify-forest-700" />
									) : null}
								</CommandItem>
							))}
						</CommandGroup>
					</CommandList>
				</Command>
			</PopoverContent>
		</Popover>
	);
};
